import orderBy from 'lodash/orderBy'
import { renderToStaticMarkup } from 'react-dom/server'
import { resolveFormatter } from '~/components/echarts/formatters'
import { TooltipCircle } from '~/components/echarts/tooltip_circle'
import { currencyFormatter } from '~/utils/currency'

function TooltipItem({ name, value, color, formatter }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
      <TooltipCircle color={color} />
      <div style={{ paddingLeft: '4px', marginRight: 'auto' }}>{name}</div>
      <div style={{ paddingLeft: '8px', fontWeight: 'bold' }}>{formatter(value)}</div>
    </div>
  )
}

function Tooltip({ title, items, formatter }) {
  return (
    <>
      {title}
      {items.map((props, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: todo: fix this
        <TooltipItem key={index} formatter={formatter} {...props} />
      ))}
    </>
  )
}

// put "Other",  "Total" and "Trend Line" at the bottom by assigning a weight to them
const nameWeight = (item) => {
  switch (item.name) {
    case 'Other':
      return 1 // second to last element
    case 'Total':
      return 2 // last element
    case 'Trend Line':
      return 3 // last element
    default:
      return 0
  }
}

export function sortStackedTooltipItems(params) {
  const items = params.map((item) => {
    return {
      name: item.seriesName,
      value: item.value[item.encode.y[0]],
      color: item.color,
    }
  })

  // sort by name weight asc, then value desc and then name asc
  return orderBy(items, [nameWeight, 'value', 'name'], ['asc', 'desc', 'asc'])
}

export function stackedTooltipFormatter(_currency, formatter) {
  const formatterFn = resolveFormatter(formatter)

  return (params) => {
    const title = params[0].axisValueLabel
    const sortedItems = sortStackedTooltipItems(params)

    return renderToStaticMarkup(<Tooltip title={title} items={sortedItems} formatter={formatterFn} />)
  }
}

const SEVERITY_ORDER = { Critical: 0, High: 1, Medium: 2, Low: 3, Informational: 4 }

function sortSeverityTooltipItems(params) {
  const items = params.map((item) => ({
    name: item.seriesName,
    value: item.value[item.encode.y[0]],
    color: item.color,
  }))

  return orderBy(items, [(item) => SEVERITY_ORDER[item.name] ?? nameWeight(item) + 100], ['asc'])
}

export function stackedTooltipFormatterWithInteger(params) {
  const title = params[0].axisValueLabel
  const sortedItems = sortSeverityTooltipItems(params)
  const formatter = (number) => Math.round(number).toLocaleString()

  return renderToStaticMarkup(<Tooltip title={title} items={sortedItems} formatter={formatter} />)
}

export function stackedTooltipFormatterWithNumber(params) {
  const title = params[0].axisValueLabel
  const sortedItems = sortStackedTooltipItems(params)
  const formatter = (number) => {
    return number.toFixed(2)
  }

  return renderToStaticMarkup(<Tooltip title={title} items={sortedItems} formatter={formatter} />)
}

export function stackedTooltipFormatterWithCurrencyPrecision(currency) {
  const formatterFn = currencyFormatter(currency, 'withPrecision')

  return (params) => {
    const title = params[0].axisValueLabel
    const sortedItems = sortStackedTooltipItems(params)

    const formatter = (number) => {
      return formatterFn(number.toFixed(5))
    }

    return renderToStaticMarkup(<Tooltip title={title} items={sortedItems} formatter={formatter} />)
  }
}

export function stackedTooltipFormatterWithUnit(unit) {
  return (params) => {
    const title = params[0].axisValueLabel
    const sortedItems = sortStackedTooltipItems(params)

    const formatter = (number) => {
      return `${Math.round(number)} ${unit}`
    }

    return renderToStaticMarkup(<Tooltip title={title} items={sortedItems} formatter={formatter} />)
  }
}

export function plainStackedTooltipFormatter(_currency, formatter) {
  const formatterFn = resolveFormatter(formatter)

  return (title, seriesData) => {
    const sortedItems = orderBy(seriesData, [nameWeight, 'value', 'name'], ['asc', 'desc', 'asc'])

    return <Tooltip title={title} items={sortedItems} formatter={formatterFn} />
  }
}
